import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/context/ToastContext";
import { api } from "@/lib/api";
import type { Task, TaskPriority, TaskType } from "@/types/task";
import {
  CheckCircle2,
  CalendarPlus,
  Clock,
  MessageSquareQuote,
  Tag,
  PhoneCall,
  CalendarCheck2,
  CalendarClock,
  Mail,
  CheckSquare2,
} from "lucide-react";

interface TaskCompleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: Task | null;
  onCompleted: () => void;
}

export function TaskCompleteDialog({
  open,
  onOpenChange,
  task,
  onCompleted,
}: TaskCompleteDialogProps) {
  const { success, error: toastError } = useToast();
  const [loading, setLoading] = useState(false);

  const [outcome, setOutcome] = useState("");
  const [scheduleNext, setScheduleNext] = useState(false);

  // Next task fields
  const [nextTitle, setNextTitle] = useState("");
  const [nextType, setNextType] = useState<TaskType>("FOLLOW_UP");
  const [nextDueAt, setNextDueAt] = useState("");
  const [nextPriority, setNextPriority] = useState<TaskPriority>("NORMAL");
  const [nextDescription, setNextDescription] = useState("");

  useEffect(() => {
    if (!open || !task) return;
    setOutcome("");
    setScheduleNext(false);

    // Default next follow-up 3 business days later
    const nextDate = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);
    nextDate.setHours(10, 0, 0, 0);
    const pad = (n: number) => n.toString().padStart(2, "0");
    const dtLocal = `${nextDate.getFullYear()}-${pad(nextDate.getMonth() + 1)}-${pad(
      nextDate.getDate()
    )}T${pad(nextDate.getHours())}:${pad(nextDate.getMinutes())}`;

    setNextTitle(
      task.related_name
        ? `Next follow-up with ${task.related_name}`
        : `Next follow-up: ${task.title}`
    );
    setNextType(task.task_type || "FOLLOW_UP");
    setNextDueAt(dtLocal);
    setNextPriority("NORMAL");
    setNextDescription("");
  }, [open, task]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!task) return;

    if (scheduleNext && !nextTitle.trim()) {
      toastError("Validation Error", "Please provide a title for the next follow-up.");
      return;
    }
    if (scheduleNext && !nextDueAt) {
      toastError("Validation Error", "Please select a due date for the next follow-up.");
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        outcome: outcome.trim() || undefined,
      };

      if (scheduleNext) {
        payload.next_task = {
          title: nextTitle.trim(),
          task_type: nextType,
          due_at: new Date(nextDueAt).toISOString(),
          priority: nextPriority,
          description: nextDescription.trim() || undefined,
        };
      }

      await api.patch(`/api/Task/Complete/${task.id}`, payload);
      success(
        "Task Completed",
        scheduleNext
          ? "Task marked done and next follow-up scheduled."
          : "Task marked done successfully."
      );
      onCompleted();
      onOpenChange(false);
    } catch (err: any) {
      toastError("Error", err.message || "Failed to complete task.");
    } finally {
      setLoading(false);
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl md:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg text-success">
            <CheckCircle2 className="size-5" />
            <span>Complete Task</span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-1">
          {/* Task Info Summary */}
          <div className="p-3 sm:p-3.5 rounded-lg border border-border bg-muted/40">
            <div className="text-xs sm:text-sm font-semibold text-foreground">{task.title}</div>
            <div className="text-[11px] sm:text-xs text-muted-foreground mt-0.5">
              Due: {new Date(task.due_at).toLocaleString()}
              {task.related_name ? ` • Linked: ${task.related_name}` : ""}
            </div>
          </div>

          {/* Outcome Notes */}
          <div>
            <Label htmlFor="outcome-notes" className="text-xs sm:text-sm font-semibold flex items-center gap-1.5">
              <MessageSquareQuote className="size-3.5 text-muted-foreground" />
              <span>Completion Outcome / Disposition Notes</span>
            </Label>
            <Textarea
              id="outcome-notes"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              placeholder="e.g. Spoke with client, positive response. Revision needed on product line items..."
              className="mt-1 resize-none"
              rows={3}
            />
          </div>

          {/* Schedule Next Follow-up Checkbox */}
          <div className="pt-2 border-t border-border">
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={scheduleNext}
                onChange={(e) => setScheduleNext(e.target.checked)}
                className="size-4 rounded border-border text-primary focus:ring-primary"
              />
              <span className="text-xs sm:text-sm font-semibold text-foreground flex items-center gap-1.5">
                <CalendarPlus className="size-4 text-primary" />
                <span>Schedule next follow-up task</span>
              </span>
            </label>

            {scheduleNext && (
              <div className="mt-3 p-3.5 rounded-lg border border-border bg-card space-y-3">
                <div>
                  <Label className="text-xs sm:text-sm font-semibold">Next Task Title</Label>
                  <Input
                    value={nextTitle}
                    onChange={(e) => setNextTitle(e.target.value)}
                    className="mt-1"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs sm:text-sm font-semibold flex items-center gap-1">
                      <Tag className="size-3.5" />
                      <span>Type</span>
                    </Label>
                    <Select
                      value={nextType}
                      onValueChange={(val: TaskType) => setNextType(val)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CALL">
                          <span className="flex items-center gap-1.5 text-xs sm:text-sm">
                            <PhoneCall className="size-3.5 text-primary" />
                            <span>Call</span>
                          </span>
                        </SelectItem>
                        <SelectItem value="FOLLOW_UP">
                          <span className="flex items-center gap-1.5 text-xs sm:text-sm">
                            <CalendarCheck2 className="size-3.5 text-primary" />
                            <span>Follow-up</span>
                          </span>
                        </SelectItem>
                        <SelectItem value="MEETING">
                          <span className="flex items-center gap-1.5 text-xs sm:text-sm">
                            <CalendarClock className="size-3.5 text-accent-foreground" />
                            <span>Meeting</span>
                          </span>
                        </SelectItem>
                        <SelectItem value="EMAIL">
                          <span className="flex items-center gap-1.5 text-xs sm:text-sm">
                            <Mail className="size-3.5 text-info" />
                            <span>Email</span>
                          </span>
                        </SelectItem>
                        <SelectItem value="OTHER">
                          <span className="flex items-center gap-1.5 text-xs sm:text-sm">
                            <CheckSquare2 className="size-3.5 text-muted-foreground" />
                            <span>Other</span>
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs sm:text-sm font-semibold flex items-center gap-1">
                      <Clock className="size-3.5" />
                      <span>Next Due Date</span>
                    </Label>
                    <Input
                      type="datetime-local"
                      value={nextDueAt}
                      onChange={(e) => setNextDueAt(e.target.value)}
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs sm:text-sm font-semibold">Notes (Optional)</Label>
                  <Input
                    placeholder="Instructions for next follow-up"
                    value={nextDescription}
                    onChange={(e) => setNextDescription(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="pt-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-success hover:bg-success/90 text-white"
              disabled={loading}
            >
              {loading ? "Completing..." : "Complete Task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
